import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth, gradeAtLeast } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import {
  LogOut,
  Moon,
  Languages,
  Menu,
  X,
  BookOpen,
  Feather,
  Scroll,
  Library as LibraryIcon,
  Sparkles,
  Sun as SunIcon,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Circle,
  Lock,
  Radio,
} from "lucide-react";

const SIDEBAR_WIDTH = "280px";

const linkBase =
  "flex items-center gap-3 px-4 py-2.5 text-[11px] tracking-[0.24em] uppercase font-semibold text-[#1A1918] hover:text-[#8B3A3A] hover:bg-[#EAE0CA]/60 transition-colors border-l-2";
const linkIdle = "border-transparent";
const linkActive = "text-[#8B3A3A] border-[#8B3A3A] bg-[#EAE0CA]/40";

/**
 * Left-hand sidebar navigation.
 * Desktop: sticky column, always visible (lg+).
 * Mobile / tablet: slide-in drawer opened via a hamburger in a slim top bar.
 * Exports both the sidebar itself and a small `MobileMenuBar` for the top bar.
 */
export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = !!user && user !== false;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [tarotOpen, setTarotOpen] = useState(location.pathname.startsWith("/tarot"));

  // Close drawer on route change (mobile)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Auto-open Tarot section when navigating into a /tarot/* route
  useEffect(() => {
    if (location.pathname.startsWith("/tarot")) setTarotOpen(true);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  const toggleLang = () => setLang(lang === "nl" ? "en" : "nl");

  const inner = (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-6 py-6 border-b border-[#C1B49A]">
        <Link to={isAuthed ? "/diary" : "/"} className="flex items-center gap-3" data-testid="sidebar-home-link">
          <span className="seal" aria-hidden>✦</span>
          <span className="font-display text-2xl tracking-tight leading-none">
            NLP <span className="italic text-[#8B3A3A]">Magick</span>
          </span>
        </Link>
      </div>

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto py-4">
        {isAuthed ? (
          <ul className="space-y-0.5">
            <SidebarLink to="/diary" testId="nav-diary-link" icon={<Feather size={14} />} label={t("nav.diary")} />
            <SidebarLink to="/rituals" testId="nav-rituals-link" icon={<Scroll size={14} />} label={t("nav.rituals")} />
            <SidebarLink to="/lectures" testId="nav-lectures-link" icon={<BookOpen size={14} />} label={t("nav.lectures")} />
            <SidebarLink to="/library" testId="nav-library-link" icon={<LibraryIcon size={14} />} label={t("nav.library")} />
            <SidebarLink to="/pantheon" testId="nav-pantheon-link" icon={<Sparkles size={14} />} label={t("nav.pantheon")} />

            {/* Tarot expandable */}
            <li>
              <button
                type="button"
                onClick={() => setTarotOpen((v) => !v)}
                data-testid="nav-tarot-toggle"
                aria-expanded={tarotOpen}
                className={`${linkBase} ${
                  location.pathname.startsWith("/tarot") ? linkActive : linkIdle
                } w-full text-left`}
              >
                <SunIcon size={14} />
                <span className="flex-1">{t("nav.tarot")}</span>
                {tarotOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
              {tarotOpen && (
                <ul className="pl-6 py-1 space-y-0.5" data-testid="nav-tarot-submenu">
                  <SidebarSubLink to="/tarot/personality-card" testId="nav-tarot-personality-link" label={t("nav.tarot_personality")} />
                  <SidebarSubLink
                    to="/tarot/celtic-cross"
                    testId="nav-tarot-celtic-link"
                    label={t("nav.tarot_celtic")}
                    locked={!(user.role === "admin" || (gradeAtLeast(user, "Neophyte") && user.tarot_course_completed))}
                  />
                  <SidebarSubLink to="/tarot/ultrasecret" testId="nav-tarot-ultrasecret-link" label={t("nav.tarot_ultrasecret")} />
                </ul>
              )}
            </li>

            <SidebarLink to="/podcasts" testId="nav-podcasts-link" icon={<Radio size={14} />} label={t("nav.podcast")} />

            {user.role === "admin" && (
              <SidebarLink to="/admin" testId="nav-admin-link" icon={<ShieldCheck size={14} />} label={t("nav.sanctum")} />
            )}
          </ul>
        ) : (
          <ul className="space-y-0.5 px-2">
            <li>
              <Link to="/login" className={`${linkBase} ${linkIdle}`} data-testid="nav-login-link">
                <span className="w-3.5" /> {t("nav.enter")}
              </Link>
            </li>
            <li>
              <Link
                to="/register"
                className="mx-4 my-4 btn-primary flex items-center gap-2"
                data-testid="nav-register-link"
              >
                <Moon size={14} /> {t("nav.initiate")}
              </Link>
            </li>
          </ul>
        )}
      </nav>

      {/* Footer of sidebar: language + account + logout */}
      <div className="px-4 py-4 border-t border-[#C1B49A] space-y-3">
        <button
          type="button"
          onClick={toggleLang}
          data-testid="nav-lang-toggle"
          className="w-full inline-flex items-center justify-center gap-2 border border-[#1A1918] px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase font-semibold hover:bg-[#EAE0CA] transition-colors"
          aria-label="Switch language"
          title={lang === "nl" ? t("lang.switch_to_en") : t("lang.switch_to_nl")}
        >
          <Languages size={12} />
          <span data-testid={`nav-lang-current-${lang}`}>{lang === "nl" ? "NL" : "EN"}</span>
          <span className="text-[#7A7061]">/</span>
          <span className="text-[#7A7061]">{lang === "nl" ? "EN" : "NL"}</span>
        </button>

        {isAuthed && (
          <>
            <NavLink to="/account" data-testid="nav-account-link" className="block px-2 py-2 hover:bg-[#EAE0CA]/60 transition-colors">
              <div className="text-xs text-[#4A4238] font-medium truncate">{user.name}</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#8B3A3A] font-semibold" data-testid="nav-user-grade">{user.grade}</div>
            </NavLink>
            <button
              data-testid="nav-logout-btn"
              onClick={handleLogout}
              className="btn-ghost w-full flex items-center justify-center gap-2"
              aria-label="Logout"
            >
              <LogOut size={14} />
              <span>{t("nav.depart")}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* --------- Desktop sticky sidebar --------- */}
      <aside
        data-testid="main-sidebar"
        className="hidden lg:flex flex-col bg-[#F4EEDF] border-r border-[#C1B49A]"
        style={{
          position: "fixed",
          top: 0,
          bottom: 0,
          left: 0,
          width: SIDEBAR_WIDTH,
          zIndex: 40,
        }}
      >
        {inner}
      </aside>

      {/* --------- Mobile top bar with hamburger --------- */}
      <div
        data-testid="mobile-navbar"
        className="lg:hidden sticky top-0 z-40 bg-[#F4EEDF]/95 backdrop-blur-md border-b border-[#C1B49A] flex items-center justify-between px-4 py-3"
      >
        <Link to={isAuthed ? "/diary" : "/"} className="flex items-center gap-2" data-testid="mobile-home-link">
          <span className="seal text-xl" aria-hidden>✦</span>
          <span className="font-display text-lg tracking-tight leading-none">
            NLP <span className="italic text-[#8B3A3A]">Magick</span>
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-open-btn"
          className="p-2 border border-[#1A1918] hover:bg-[#EAE0CA] transition-colors"
          aria-label="Open menu"
        >
          <Menu size={18} />
        </button>
      </div>

      {/* --------- Mobile drawer overlay --------- */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          data-testid="mobile-drawer-overlay"
          onClick={() => setMobileOpen(false)}
          role="button"
          tabIndex={-1}
        >
          <div className="absolute inset-0 bg-[#1A1918]/40" />
          <div
            className="absolute inset-y-0 left-0 bg-[#F4EEDF] border-r border-[#C1B49A] flex flex-col animate-slide-in"
            style={{ width: SIDEBAR_WIDTH, maxWidth: "85vw" }}
            data-testid="mobile-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close-btn"
              className="absolute top-4 right-4 z-10 p-2 border border-[#1A1918] bg-[#F4EEDF] hover:bg-[#EAE0CA] transition-colors"
              aria-label="Close menu"
            >
              <X size={16} />
            </button>
            {inner}
          </div>
        </div>
      )}
    </>
  );
}

function SidebarLink({ to, testId, icon, label }) {
  return (
    <li>
      <NavLink
        to={to}
        data-testid={testId}
        className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
      >
        {icon}
        <span>{label}</span>
      </NavLink>
    </li>
  );
}

function SidebarSubLink({ to, testId, label, locked }) {
  return (
    <li>
      <NavLink
        to={to}
        data-testid={testId}
        className={({ isActive }) =>
          `flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] uppercase text-[#4A4238] hover:text-[#8B3A3A] hover:bg-[#EAE0CA]/60 transition-colors border-l-2 ${
            isActive ? "text-[#8B3A3A] border-[#8B3A3A]" : "border-transparent"
          }`
        }
      >
        <Circle size={6} className="fill-current" />
        <span className="flex-1">{label}</span>
        {locked && <Lock size={10} className="text-[#8B3A3A]" data-testid={`${testId}-lock`} />}
      </NavLink>
    </li>
  );
}
